// src/pages/ExamPortal.jsx

import React, {
  useEffect,
  useMemo,
  useState,
  useRef
} from "react";
import { useParams } from "react-router-dom";

import axios from "axios";

import {
  Layout,
  Card,
  Typography,
  Input,
  Button,
  Row,
  Col,
  Space,
  Progress,
  message,
  Tag,
  Spin,
  Avatar,
  Divider,
  Grid,
  Badge,
  Modal,
} from "antd";

import {
  UserOutlined,
  PhoneOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  TrophyFilled,
  LeftOutlined,
  RightOutlined,
  SendOutlined,
  ReloadOutlined,
  CheckOutlined,
  WarningFilled,
} from "@ant-design/icons";

import {
  motion,
} from "framer-motion";

import BASE_URL from "../services/service";


const {
  Header,
  Content,
} = Layout;

const {
  Title,
  Text,
} = Typography;

const {
  useBreakpoint,
} = Grid;

export default function ExamPortal() {
  const { paperId } = useParams();
  const screens = useBreakpoint();
  const EXAM_STORAGE_KEY = `exam_${paperId}`;
  const TIMER_STORAGE_KEY = `exam_timer_${paperId}`;
  const VIOLATION_STORAGE_KEY = `exam_violation_${paperId}`;
  const savedExam = localStorage.getItem(EXAM_STORAGE_KEY);



  const [showQuestionPalette, setShowQuestionPalette] =
    useState(false);

  const [submitted, setSubmitted] =
    useState(false);

  const [examPin, setExamPin] =
    useState("");

  const [paper, setPaper] = useState(null);

  const [pinVerified, setPinVerified] =
    useState(false);


  const [step, setStep] =
    useState("welcome");

  const [loading, setLoading] =
    useState(false);

  const [fullName, setFullName] =
    useState("");

  const [mobile, setMobile] =
    useState("");

  const [visitedQuestions, setVisitedQuestions] =
    useState([0]);

  const [student, setStudent] =
    useState(null);

  const [question, setQuestion] =
    useState(null);

  const [selectedAnswer, setSelectedAnswer] =
    useState(null);

  const [currentIndex, setCurrentIndex] =
    useState(0);

  const [totalQuestions, setTotalQuestions] =
    useState(60);

const hasEnteredFullscreen = useRef(false);
const ignoreNextFullscreenEvent = useRef(false);

const visibilityTimeout = useRef(null);
const examStartedAt = useRef(null);
const isFullscreenTransition = useRef(false);
const forceSubmitting = useRef(false);

  const [reloadAttempts, setReloadAttempts] = useState(
    Number(localStorage.getItem(`reload_${paperId}`)) || 0
  );

  const [reviewData, setReviewData] =
    useState({
      answered: [],
      unanswered: [],
    });

  const [saving, setSaving] =
    useState(false);

  const [timer, setTimer] =
    useState(0);



const enterFullScreen = async () => {
  try {
    isFullscreenTransition.current = true;

    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
    }

    setTimeout(() => {
      isFullscreenTransition.current = false;
    }, 1500);
  } catch (err) {
    isFullscreenTransition.current = false;
    console.log(err);
  }
};

  useEffect(() => {
    if (step !== "exam") return;

    ignoreNextFullscreenEvent.current = false;

    const handleExit = () => {
      if (ignoreNextFullscreenEvent.current) return;

      if (
        hasEnteredFullscreen.current &&
        !document.fullscreenElement
      ) {
        registerViolation(
          "You exited fullscreen during examination."
        );
      }
    };

    document.addEventListener(
      "fullscreenchange",
      handleExit
    );

    return () => {
      ignoreNextFullscreenEvent.current = true;

      document.removeEventListener(
        "fullscreenchange",
        handleExit
      );
    };
  }, [step]);

  useEffect(() => {
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.userSelect = "auto";
    };
  }, []);

  useEffect(() => {
    fetchPaper();


    if (savedExam) {
      const parsed = JSON.parse(savedExam);

      setStep(parsed.step || "welcome");
      setStudent(parsed.student || null);
      setCurrentIndex(parsed.currentIndex || 0);
      setFullName(parsed.fullName || "");
      setMobile(parsed.mobile || "");
      setVisitedQuestions(parsed.visitedQuestions || [0]);
      setPinVerified(parsed.pinVerified || false);
      setReviewData(
        parsed.reviewData || {
          answered: [],
          unanswered: [],
        }
      );
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      EXAM_STORAGE_KEY,
      JSON.stringify({
        step,
        student,
        currentIndex,
        fullName,
        mobile,
        visitedQuestions,
        pinVerified,
        reviewData,
      })
    );
  }, [
    step,
    student,
    currentIndex,
    fullName,
    mobile,
    visitedQuestions,
    pinVerified,
    reviewData,
  ]);


  const fetchPaper = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/paper/${paperId}`
      );

      if (res.data.success) {
        setPaper(res.data.paper);

        const totalSeconds =
          (res.data.paper.durationMinutes || 60) * 60;

        const savedTimer =
          localStorage.getItem(TIMER_STORAGE_KEY);

        setTimer(savedTimer ? Number(savedTimer) : totalSeconds);
      }
    } catch (err) {
      console.log(err);

      message.error("Failed to load paper");
    }
  };

  const progressPercent = useMemo(() => {
    return Number(
      (
        ((currentIndex + 1) /
          totalQuestions) *
        100
      ).toFixed(2)
    );
  }, [currentIndex, totalQuestions]);

  useEffect(() => {
    if (step !== "exam" || !pinVerified) return;

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          localStorage.removeItem(TIMER_STORAGE_KEY);
          handleFinalSubmit();
          return 0;
        }

        const newTime = prev - 1;

        localStorage.setItem(
          TIMER_STORAGE_KEY,
          newTime
        );

        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [step, pinVerified]);


  const reloadChecked = useRef(false);

  useEffect(() => {
    if (step !== "exam") return;

    if (reloadChecked.current) return;
    reloadChecked.current = true;

    const navEntries = performance.getEntriesByType("navigation");

    if (
      navEntries.length &&
      navEntries[0].type === "reload"
    ) {
      const storedReload =
        Number(localStorage.getItem(`reload_${paperId}`)) || 0;

      const newReload = storedReload + 1;

      localStorage.setItem(`reload_${paperId}`, newReload);
      setReloadAttempts(newReload);

      if (newReload >= 3) {
        handleForceSubmit(
          "Repeated page reload attempts detected."
        );
      } else {
        Modal.error({
          title: "Reload Attempt Detected",
          centered: true,
          content:
            `Reloading the exam page is prohibited. Attempt ${newReload} of 3.`
        });
      }
    }
  }, [step, paperId]);

  useEffect(() => {
    if (step === "exam") {
      window.history.pushState(null, "", window.location.href);

      const blockBack = () => {
        window.history.pushState(
          null,
          "",
          window.location.href
        );

        message.warning(
          "Back navigation is disabled during exam."
        );
      };

      window.addEventListener("popstate", blockBack);

      return () =>
        window.removeEventListener(
          "popstate",
          blockBack
        );
    }
  }, [step]);

  const [violations, setViolations] = useState(
    Number(localStorage.getItem(VIOLATION_STORAGE_KEY)) || 0
  );


  const [violationReason, setViolationReason] = useState("");

  const [screenBlocked, setScreenBlocked] = useState(false);
  useEffect(() => {
    if (step !== "exam") return;

    const handleKeyDown = (e) => {
      if (
        e.key === "PrintScreen" ||
        (e.metaKey && e.shiftKey && e.key === "3")
      ) {
        e.preventDefault();
        registerViolation(
          "Attempted to capture exam content via screenshot."
        );
      }

      if (
        (e.ctrlKey && ["c", "x", "u", "s"].includes(e.key.toLowerCase())) ||
        (e.ctrlKey && e.shiftKey && ["i", "j"].includes(e.key.toLowerCase())) ||
        e.key === "F12"
      ) {
        e.preventDefault();
        registerViolation(
          "Restricted keyboard shortcut detected."
        );
      }
    };


const handleVisibility = () => {
  const now = Date.now();

  // Ignore first 3 seconds after exam starts
  if (
    examStartedAt.current &&
    now - examStartedAt.current < 3000
  ) {
    return;
  }

  // Ignore fullscreen transitions
  if (isFullscreenTransition.current) {
    return;
  }

  if (document.hidden) {
    visibilityTimeout.current = setTimeout(() => {
      // Only count if still hidden after delay
      if (document.hidden) {
        registerViolation(
          "Tab switching or minimizing detected."
        );
      }
    }, 1500); // wait 1.5 sec
  } else {
    // User returned quickly → cancel violation
    if (visibilityTimeout.current) {
      clearTimeout(visibilityTimeout.current);
    }
  }
};

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [step, violations]);

  const registerViolation = (reason) => {
    setViolations((prev) => {
      const newCount = prev + 1;

      localStorage.setItem(
        VIOLATION_STORAGE_KEY,
        newCount
      );

      setViolationReason(reason);

      Modal.warning({
        title: `Security Violation (${newCount}/3)`,
        centered: true,
        okText: "I Understand",
        content: (
          <div>
            <p>{reason}</p>
            <p>This exam is under strict monitoring.</p>
            <p>3 violations will auto-submit your exam.</p>
          </div>
        )
      });

      if (newCount >= 3 && !forceSubmitting.current) {
  forceSubmitting.current = true;
  handleForceSubmit(reason);
}

      return newCount;
    });
  };

  const handleForceSubmit = async (reason) => {
    setViolationReason(reason);

    Modal.error({
      title: "Exam Terminated",
      centered: true,
      okButtonProps: { danger: true },
      content: (
        <div>
          <p>
            Your examination has been automatically submitted.
          </p>
          <p>
            Reason: {reason}
          </p>
          <p>
            Multiple unauthorized activities were detected.
          </p>
        </div>
      ),
      onOk: () => handleFinalSubmit()
    });
  };

  const handleStartExam = async () => {
    try {
      if (!fullName) {
        return message.error(
          "Enter full name"
        );
      }

      if (mobile.length !== 10) {
        return message.error(
          "Enter valid mobile number"
        );
      }

      setLoading(true);

      const res = await axios.post(
        `${BASE_URL}/start-exam`,
        {
          fullName,
          mobile,
          paperId
        }
      );

      if (res.data.success) {
        setStudent({
          devoteeId:
            res.data.devoteeId,

          studentId:
            res.data.studentId,
        });

        setCurrentIndex(
          res.data.currentQuestionIndex || 0
        );

        setTotalQuestions(
          res.data.totalQuestions || 60
        );
        setFullName(res.data?.existingFullName || '')

        setStep("pin");
        setVisitedQuestions([
          res.data.currentQuestionIndex ||
          0,
        ]);

        fetchQuestion(
          res.data.studentId,
          res.data.currentQuestionIndex || 0
        );

        message.success(
          `Welcome ${fullName}`
        );
      }
    } catch (err) {
      console.log(err);

      message.error(
        "Unable to start exam"
      );
    } finally {
      setLoading(false);
    }
  };


  const formattedTime = useMemo(() => {
    const mins = Math.floor(timer / 60);

    const secs = timer % 60;

    return `${mins
      .toString()
      .padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
  }, [timer]);



  // START EXAM
  const handleVerifyPin = async () => {

    if (examPin !== paper?.examPin) {
      return message.error(
        "Invalid Exam PIN"
      );
    }

    setPinVerified(true);

   examStartedAt.current = Date.now();
setStep("exam");

setTimeout(async () => {
  await enterFullScreen();
}, 300);

    message.success(
      "PIN Verified Successfully"
    );
  };

  // JUMP TO QUESTION
  const jumpToQuestion = (
    questionIndex
  ) => {
    setCurrentIndex(questionIndex);

    fetchQuestion(
      student.studentId,
      questionIndex
    );

    setVisitedQuestions((prev) => [
      ...new Set([
        ...prev,
        questionIndex,
      ]),
    ]);
  };

  // FETCH QUESTION
  const fetchQuestion = async (
    studentId,
    index
  ) => {
    try {
      setLoading(true);

      const url =
        window.location.hostname.includes(
          "localhost"
        )
          ? "http://localhost:3001/api/exams/fetch-question"
          : "https://api.iskconsolapur.org/api/exams/fetch-question";

      const res = await axios.get(url, {
        params: {
          studentId,
          questionIndex: index,
          paperId,
        },
      });

      if (res.data.success) {
        setQuestion(res.data.question);

        setSelectedAnswer(
          res.data.selectedAnswer
        );

        if (
          res.data.selectedAnswer !==
          null
        ) {
          setReviewData((prev) => ({
            ...prev,
            answered: [
              ...new Set([
                ...prev.answered,
                index,
              ]),
            ],
          }));
        }
      }
    } catch (err) {
      console.log(err);

      message.error(
        "Failed to fetch question"
      );
    } finally {
      setLoading(false);
    }
  };

  // SAVE ANSWER
  const saveAnswer = async (
    optionIndex,
    indexToSave = currentIndex
  ) => {
    try {
      setSaving(true);

      setSelectedAnswer(optionIndex);

      const url =
        window.location.hostname.includes(
          "localhost"
        ) ?
          "http://localhost:3001/api/exams/answer-for-question" :
          "https://api.iskconsolapur.org/api/exams/answer-for-question";

      console.log(
        "Saving Question Index:",
        indexToSave
      );

      await axios.post(url, {
        studentId: student.studentId,

        selectedOption: optionIndex,

        questionIndex: indexToSave,
        paperId,
      });

      setReviewData((prev) => ({
        ...prev,

        answered: [
          ...new Set([
            ...prev.answered,
            indexToSave,
          ]),
        ],
      }));

      message.success(
        "Answer Saved"
      );
    } catch (err) {
      console.log(err);

      message.error(
        "Save failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // NEXT
  const nextQuestion = () => {
    if (
      currentIndex + 1 >=
      totalQuestions
    ) {
      return setStep("review");
    }

    const next = currentIndex + 1;

    setCurrentIndex(next);

    setVisitedQuestions((prev) => [
      ...new Set([
        ...prev,
        next,
      ]),
    ]);

    fetchQuestion(
      student.studentId,
      next
    );
  };

  // PREVIOUS
  const previousQuestion = () => {
    if (currentIndex <= 0) return;

    const prev = currentIndex - 1;

    setCurrentIndex(prev);

    setVisitedQuestions((prevState) => [
      ...new Set([
        ...prevState,
        prev,
      ]),
    ]);

    fetchQuestion(
      student.studentId,
      prev
    );
  };

  // REVIEW FETCH
  const fetchReview = async () => {
    try {
      const url =
        window.location.hostname.includes(
          "localhost"
        )
          ? "http://localhost:3001/api/exams/final-review"
          : "https://api.iskconsolapur.org/api/exams/final-review";

      const res = await axios.get(url, {
        params: {
          studentId:
            student.studentId,
          paperId
        },
      });

      if (res.data.success) {
        setReviewData({
          answered:
            res.data.answeredQuestions,

          unanswered:
            res.data.unansweredQuestions,
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (step === "review") {
      fetchReview();
    }
  }, [step]);

  const handleFinalSubmit = async () => {
    try {

      if (submitted) return;

      setSubmitted(true);

      setLoading(true);

      const url =
        window.location.hostname.includes(
          "localhost"
        )
          ? "http://localhost:3001/api/exams/final-submit"
          : "https://api.iskconsolapur.org/api/exams/final-submit";

      const res = await axios.post(url, {
        studentId:
          student.studentId,
        paperId,
      });

      if (res.data.success) {

        ignoreNextFullscreenEvent.current = true;

setTimeout(async () => {
  if (document.fullscreenElement) {
    await document.exitFullscreen();
  }
}, 100);
        Modal.success({
          title:
            "Thank You 🙏",

          centered: true,

          okText: "Close",

          maskClosable: false,

          content: (
            <div
              style={{
                marginTop: 20,
                textAlign: "center",
              }}
            >
              <h2
                style={{
                  color: "#16a34a",
                }}
              >
                Exam Submitted Successfully
              </h2>

              <p>
                Hare Krishna 🙏
              </p>
            </div>
          ),

         onOk: () => {
  localStorage.removeItem(EXAM_STORAGE_KEY);
  localStorage.removeItem(TIMER_STORAGE_KEY);
  localStorage.removeItem(VIOLATION_STORAGE_KEY);
  localStorage.removeItem(`reload_${paperId}`);
  window.location.reload();
}
        });
      }
    } catch (err) {

      setSubmitted(false);

      console.log(err);

      message.error(
        err?.response?.data?.message ||
        "Failed to submit exam"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!paper) {
    return (
      <Layout
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin size="large" />
      </Layout>
    );
  }


  // WELCOME SCREEN
  if (step === "welcome") {
    return (
      <Layout
        style={{
          minHeight: "100vh",

          background:
            "linear-gradient(135deg,#ff9a9e 0%,#fad0c4 100%)",
        }}
      >
        <Content
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 20,
          }}
        >
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.9,
            }}
            animate={{
              opacity: 1,
              scale: 1,
            }}
          >
            <Card
              style={{
                width:
                  screens.xs
                    ? "100%"
                    : 550,

                borderRadius: 30,

                overflow: "hidden",

                boxShadow:
                  "0 20px 50px rgba(0,0,0,0.15)",

                border: "none",
              }}
              bodyStyle={{
                padding: 40,
              }}
            >
              <Space
                direction="vertical"
                size={24}
                style={{
                  width: "100%",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                  }}
                >
                  <Avatar
                    size={100}
                    style={{
                      background:
                        "linear-gradient(135deg,#ff6a00,#ee0979)",

                      fontSize: 40,
                    }}
                  >
                    <TrophyFilled />
                  </Avatar>

                  <Title
                    level={2}
                    style={{
                      marginTop: 20,
                      marginBottom: 0,
                    }}
                  >
                    ISKCON Solapur
                  </Title>
                  <Title
                    level={3}
                    style={{
                      marginTop: 30,
                      marginBottom: 0,
                    }}
                  >
                    {paper?.title}

                  </Title>

                  <Title
                    level={4}
                    style={{
                      color: "#666",
                    }}
                  >
                    Examination Portal
                  </Title>

                  <Text
                    type="secondary"
                  >
                    Smart Online Assessment
                    System
                  </Text>
                </div>

                <Input
                  size="large"
                  placeholder="Enter Full Name"
                  prefix={
                    <UserOutlined />
                  }
                  value={fullName}
                  onChange={(e) =>
                    setFullName(
                      e.target.value
                    )
                  }
                  style={{
                    borderRadius: 14,
                    height: 55,
                  }}
                />

                <Input
                  size="large"
                  placeholder="Enter Mobile Number"
                  prefix={
                    <PhoneOutlined />
                  }
                  maxLength={10}
                  value={mobile}
                  onChange={(e) =>
                    setMobile(
                      e.target.value.replace(
                        /\D/g,
                        ""
                      )
                    )
                  }
                  style={{
                    borderRadius: 14,
                    height: 55,
                  }}
                />

                <Button
                  type="primary"
                  size="large"
                  loading={loading}
                  onClick={
                    handleStartExam
                  }
                  style={{
                    height: 60,

                    borderRadius: 16,

                    fontSize: 18,

                    fontWeight: 700,

                    background:
                      "linear-gradient(135deg,#ff512f,#dd2476)",

                    border: "none",
                  }}
                >
                  Start Examination
                </Button>
              </Space>
            </Card>
          </motion.div>
        </Content>
      </Layout>
    );
  }

  if (step === "pin") {
    return (
      <Layout
        style={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg,#c2e9fb 0%,#a1c4fd 100%)",
        }}
      >
        <Content
          style={{
            display: "flex",
            justifyContent:
              "center",
            alignItems: "center",
            padding: 20,
          }}
        >
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.9,
            }}
            animate={{
              opacity: 1,
              scale: 1,
            }}
          >
            <Card
              style={{
                width:
                  screens.xs
                    ? "100%"
                    : 500,

                borderRadius: 28,

                boxShadow:
                  "0 20px 50px rgba(0,0,0,0.12)",

                border: "none",
              }}
              bodyStyle={{
                padding: 40,
              }}
            >
              <Space
                direction="vertical"
                size={24}
                style={{
                  width: "100%",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                  }}
                >
                  <Avatar
                    size={90}
                    style={{
                      background:
                        "linear-gradient(135deg,#667eea,#764ba2)",
                      fontSize: 34,
                    }}
                  >
                    🔒
                  </Avatar>

                  <Title
                    level={2}
                    style={{
                      marginTop: 20,
                      marginBottom: 0,
                    }}
                  >
                    Enter Exam PIN
                  </Title>

                  <Text type="secondary">
                    Please enter the secure
                    PIN to begin your exam
                  </Text>
                </div>

                <Input.Password
                  size="large"
                  placeholder="Enter Exam PIN"
                  value={examPin}
                  onChange={(e) =>
                    setExamPin(
                      e.target.value
                    )
                  }
                  onPressEnter={
                    handleVerifyPin
                  }
                  style={{
                    borderRadius: 14,
                    height: 55,
                    textAlign: "center",
                    fontSize: 20,
                    letterSpacing: 6,
                  }}
                />

                <Button
                  type="primary"
                  size="large"
                  onClick={
                    handleVerifyPin
                  }
                  style={{
                    height: 58,
                    borderRadius: 16,
                    fontSize: 18,
                    fontWeight: 700,
                    background:
                      "linear-gradient(135deg,#667eea,#764ba2)",
                    border: "none",
                  }}
                >
                  Verify & Start Exam
                </Button>
              </Space>
            </Card>
          </motion.div>
        </Content>
      </Layout>
    );
  }


  // REVIEW SCREEN
  if (step === "review") {
    return (
      <Layout
        style={{
          minHeight: "100vh",

          background:
            "#f5f7fb",
        }}
      >
        <Content
          style={{
            padding: 24,
          }}
        >
          <Card
            style={{
              borderRadius: 24,
            }}
          >
            <Title level={2}>
              Final Review
            </Title>

            <Divider />

            <Row gutter={[16, 16]}>
              {Array.from({
                length: totalQuestions,
              }).map((_, i) => {
                const answered =
                  reviewData.answered.includes(
                    i
                  );

                return (
                  <Col
                    key={i}
                    xs={6}
                    sm={4}
                    md={3}
                    lg={2}
                  >
                    <Button
                      block
                      style={{
                        height: 55,

                        borderRadius: 14,

                        background:
                          answered
                            ? "#16a34a"
                            : "#ff4d4f",

                        color: "#fff",

                        fontWeight: 700,
                      }}
                      onClick={() => {
                        setCurrentIndex(
                          i
                        );

                        setStep(
                          "exam"
                        );

                        fetchQuestion(
                          student.studentId,
                          i
                        );
                      }}
                    >
                      {i + 1}
                    </Button>
                  </Col>
                );
              })}
            </Row>

            <Divider />

            <Button
              type="primary"
              size="large"
              icon={<SendOutlined />}
              onClick={
                handleFinalSubmit
              }
              style={{
                height: 55,

                borderRadius: 16,

                width: "100%",
              }}
            >
              Submit Final Exam
            </Button>
          </Card>
        </Content>
      </Layout>
    );
  }

  const splitLanguages = (text = "") => {
    const parts = text.split(" / ");

    return {
      english: parts[0] || "",
      marathi: parts[1] || "",
      hindi: parts[2] || "",

    };
  };

  // EXAM SCREEN
  return (
    <Layout
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        minHeight: "100vh",

        background:
          "linear-gradient(135deg,#e0c3fc 0%,#8ec5fc 100%)",
      }}
    >

      {screenBlocked && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "#000",
            zIndex: 999999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "#fff",
            fontSize: 28,
            fontWeight: 700,
          }}
        >
          Screenshots are not allowed 🚫
        </div>
      )}

      <Header
        style={{
          background: "rgba(255,255,255,0.75)",

          backdropFilter: "blur(18px)",

          borderBottom:
            "1px solid rgba(0,0,0,0.06)",

          display: "flex",

          justifyContent:
            "space-between",

          alignItems: "center",

          padding:
            screens.xs
              ? "0 14px"
              : "0 26px",

          height: 74,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Text
            style={{
              fontSize: screens.xs
                ? 16
                : 18,

              fontWeight: 700,

              color: "#1f2937",
            }}
          >
            Hare Krishna, {fullName}
          </Text>

          <Text
            style={{
              color: "#6b7280",

              fontSize: 13,

              marginTop: -2,
            }}
          >
            Devotee ID:{" "}
            {student?.devoteeId}
          </Text>
        </div>

        <Tag
          color="green"
          icon={<ClockCircleFilled />}
          style={{
            padding: "8px 16px",

            borderRadius: 999,

            fontSize: 15,

            fontWeight: 600,
          }}
        >
          {formattedTime}
        </Tag>
      </Header>

      <Card
        style={{
          marginBottom: 16,
          borderRadius: 16,
          border: "2px solid #ef4444",
          background: "#fff1f2"
        }}
      >
        <Space>
          <WarningFilled style={{ color: "#dc2626", fontSize: 22 }} />
          <div>
            <Text strong style={{ color: "#991b1b" }}>
              Strict Examination Monitoring Enabled
            </Text>
            <br />
            <Text type="secondary">
              Screenshot, copy, tab switching, refresh, developer tools and navigation are monitored.
              3 violations will auto-submit your exam.
            </Text>
          </div>
        </Space>
      </Card>

      <Content
        style={{
          padding:
            screens.xs
              ? 12
              : 30,
        }}
      >
        <Spin spinning={loading}>
          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
          >
            <Card
              style={{
                borderRadius: 30,

                border: "none",

                overflow: "hidden",

                boxShadow:
                  "0 15px 40px rgba(0,0,0,0.12)",
              }}
              bodyStyle={{
                padding:
                  screens.xs
                    ? 20
                    : 40,
              }}
            >
              <Space
                direction="vertical"
                size={25}
                style={{
                  width: "100%",
                }}
              >
                <div>
                  <Space
                    style={{
                      width: "100%",
                      justifyContent:
                        "space-between",
                    }}
                  >
                    <Title level={4}>
                      Question{" "}
                      {currentIndex +
                        1}
                    </Title>

                    <Badge
                      count={
                        saving
                          ? "Saving..."
                          : "Saved"
                      }
                    />
                  </Space>

                  <Progress
                    percent={
                      progressPercent
                    }
                    strokeColor={{
                      from: "#108ee9",
                      to: "#87d068",
                    }}
                  />
                </div>

                <Card
                  style={{
                    borderRadius: 22,

                    background:
                      "rgba(255,255,255,0.75)",

                    border:
                      "1px solid rgba(0,0,0,0.05)",

                    boxShadow:
                      "0 4px 20px rgba(0,0,0,0.04)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent:
                        "space-between",
                      alignItems: "center",
                      marginBottom:
                        showQuestionPalette
                          ? 20
                          : 0,
                    }}
                  >
                    <div>
                      <Title
                        level={5}
                        style={{
                          margin: 0,
                        }}
                      >
                        Questions Navigator
                      </Title>

                      <Text
                        type="secondary"
                      >
                        Current:{" "}
                        {currentIndex + 1} /{" "}
                        {totalQuestions}
                      </Text>
                    </div>

                    <Button
                      type="text"
                      icon={
                        showQuestionPalette ? (
                          <MenuFoldOutlined />
                        ) : (
                          <MenuUnfoldOutlined />
                        )
                      }
                      onClick={() =>
                        setShowQuestionPalette(
                          !showQuestionPalette
                        )
                      }
                      style={{
                        borderRadius: 12,
                      }}
                    >
                      {showQuestionPalette
                        ? "Hide"
                        : "Show"}
                    </Button>
                  </div>

                  {showQuestionPalette && (
                    <>
                      <Space
                        wrap
                        size={[10, 10]}
                      >
                        {Array.from({
                          length: totalQuestions,
                        }).map((_, i) => {
                          const answered =
                            reviewData.answered.includes(
                              i
                            );

                          const visited =
                            visitedQuestions.includes(
                              i
                            );

                          const active =
                            currentIndex === i;

                          return (
                            <Button
                              key={i}
                              shape="circle"
                              onClick={() =>
                                jumpToQuestion(i)
                              }
                              style={{
                                width: 42,

                                height: 42,

                                fontWeight: 700,

                                borderRadius: "50%",

                                transition:
                                  "0.25s",

                                border: active
                                  ? "2px solid #2563eb"
                                  : answered
                                    ? "1px solid #bbf7d0"
                                    : visited
                                      ? "1px solid #d1d5db"
                                      : "1px solid #e5e7eb",

                                background: active
                                  ? "#2563eb"
                                  : answered
                                    ? "#dcfce7"
                                    : visited
                                      ? "#f3f4f6"
                                      : "#fff",

                                color: active
                                  ? "#fff"
                                  : answered
                                    ? "#166534"
                                    : "#374151",

                                boxShadow: active
                                  ? "0 6px 18px rgba(37,99,235,0.25)"
                                  : "none",
                              }}
                            >
                              {i + 1}
                            </Button>
                          );
                        })}
                      </Space>

                      <Divider />

                      <Space wrap>
                        <Tag color="blue">
                          Current
                        </Tag>

                        <Tag color="green">
                          Answered
                        </Tag>

                        <Tag>
                          Visited
                        </Tag>
                      </Space>
                    </>
                  )}
                </Card>


                <Card
                  style={{
                    borderRadius: 24,

                    background:
                      "linear-gradient(135deg,#ffffff 0%,#f8fafc 100%)",

                    border:
                      "1px solid rgba(0,0,0,0.05)",

                    userSelect: "none",

                    WebkitUserSelect: "none",

                    MozUserSelect: "none",

                    msUserSelect: "none",
                  }}

                  onCopy={(e) =>
                    e.preventDefault()
                  }

                  onCut={(e) =>
                    e.preventDefault()
                  }

                  onContextMenu={(e) =>
                    e.preventDefault()
                  }
                >
                  {(() => {
                    const q = splitLanguages(
                      question?.question
                    );

                    return (
                      <div>
                        <Title
                          level={3}
                          style={{
                            marginBottom: 8,
                            color: "#111827",
                            lineHeight: 1.5,
                          }}
                        >
                          {q.english}
                        </Title>

                        <Text
                          style={{
                            fontSize: 20,
                            color: "#6b7280",
                            lineHeight: 1.8,
                            fontWeight: 500,
                          }}
                        >
                          {q.marathi}
                        </Text>
                        <Text
                          style={{
                            display: "block",
                            marginTop: 8,
                            fontSize: 20,
                            color: "#7c3aed",
                            lineHeight: 1.8,
                            fontWeight: 500,
                          }}
                        >
                          {q.hindi}
                        </Text>
                      </div>
                    );
                  })()}
                </Card>

                <Row gutter={[16, 16]}>
                  {question?.options?.map(
                    (
                      option,
                      index
                    ) => {
                      const active =
                        selectedAnswer ===
                        index;

                      return (
                        <Col
                          xs={24}
                          md={12}
                          key={index}
                        >
                          <motion.div
                            whileHover={{
                              scale: 1.02,
                            }}
                          >
                            <Card
                              onClick={() => saveAnswer(index, currentIndex)}
                              style={{
                                cursor:
                                  "pointer",

                                borderRadius: 18,

                                border:
                                  active
                                    ? "2px solid #722ed1"
                                    : "1px solid #eee",

                                background:
                                  active
                                    ? "linear-gradient(135deg,#d299c2 0%,#fef9d7 100%)"
                                    : "#fff",

                                transition:
                                  "0.3s",
                              }}
                            >
                              <Space>
                                <div
                                  style={{
                                    width: 18,
                                    height: 18,
                                    minWidth: 18,
                                    borderRadius: "50%",

                                    border: active
                                      ? "5px solid #16a34a"
                                      : "2px solid #cbd5e1",

                                    background: active
                                      ? "#dcfce7"
                                      : "#fff",

                                    transition: "0.25s",
                                  }}
                                />

                                {(() => {
                                  const opt =
                                    splitLanguages(option);

                                  return (
                                    <div>
                                      <Text
                                        strong
                                        style={{
                                          display: "block",
                                          fontSize: 16,
                                          color: "#111827",
                                        }}
                                      >
                                        {opt.english}
                                      </Text>

                                      <Text
                                        style={{
                                          display: "block",
                                          marginTop: 4,
                                          fontSize: 15,
                                          color: "#6b7280",
                                          fontWeight: 500,
                                          lineHeight: 1.6,
                                        }}
                                      >
                                        {opt.marathi}
                                      </Text>
                                      <Text style={{ display: "block" }}>
                                        {opt.hindi}
                                      </Text>
                                    </div>
                                  );
                                })()}
                              </Space>
                            </Card>
                          </motion.div>
                        </Col>
                      );
                    }
                  )}
                </Row>

                <Divider />

                <Row
                  gutter={[16, 16]}
                >
                  <Col
                    xs={24}
                    sm={8}
                  >
                    <Button
                      icon={
                        <LeftOutlined />
                      }
                      block
                      size="large"
                      onClick={
                        previousQuestion
                      }
                      disabled={
                        currentIndex ===
                        0
                      }
                      style={{
                        height: 55,

                        borderRadius: 14,
                      }}
                    >
                      Previous
                    </Button>
                  </Col>

                  <Col
                    xs={24}
                    sm={8}
                  >
                    <Button
                      icon={
                        <ReloadOutlined />
                      }
                      block
                      size="large"
                      onClick={() =>
                        fetchQuestion(
                          student.studentId,
                          currentIndex
                        )
                      }
                      style={{
                        height: 55,

                        borderRadius: 14,
                      }}
                    >
                      Refresh
                    </Button>
                  </Col>

                  <Col
                    xs={24}
                    sm={8}
                  >
                    <Button
                      type="primary"
                      icon={
                        <RightOutlined />
                      }
                      block
                      size="large"
                      onClick={
                        nextQuestion
                      }
                      style={{
                        height: 55,

                        borderRadius: 14,

                        background:
                          "linear-gradient(135deg,#11998e,#38ef7d)",

                        border: "none",
                      }}
                    >
                      {currentIndex +
                        1 ===
                        totalQuestions
                        ? "Review"
                        : "Next"}
                    </Button>
                  </Col>
                </Row>
              </Space>
            </Card>
          </motion.div>
        </Spin>
      </Content>
    </Layout>
  );
}